import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  getRoles,
  getDepartments,
  createRole,
  updateRole,
  deleteRole,
  createDepartment,
  updateDepartment,
  deleteDepartment,
} from '../../lib/api';
import { DeleteModal } from '../../components/ui/ActionModals';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Plus, Pencil, Trash2, Shield, Building2 } from 'lucide-react';
import { toast } from 'sonner';

// Categorized permissions for better management
export const PERMISSION_GROUPS = [
  {
    title: 'Leads & Follow-ups',
    perms: ['leads.view', 'leads.edit'],
  },
  {
    title: 'Loan File Login',
    perms: ['loans.view', 'loans.edit'],
  },
  {
    title: 'Agents & Performance',
    perms: ['agents.view', 'agents.edit', 'commission.view'],
  },
  {
    title: 'Document Verification',
    perms: ['documents.view', 'documents.verify'],
  },
  {
    title: 'Management & Reports',
    perms: ['employees.view', 'dashboard.view', 'reports.view', 'reports.export'],
  },
];

// Flat list for validation if needed
export const PERMISSION_OPTIONS = PERMISSION_GROUPS.flatMap(g => g.perms);

const RolesSection = () => {
  const { token } = useAuth();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [form, setForm] = useState({ name: '', permissions: [] });
  const [submitting, setSubmitting] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ open: false, role: null });

  const fetchRoles = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await getRoles(token);
      setRoles(res?.data?.items ?? []);
    } catch {
      setRoles([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const openCreate = () => {
    setEditingRole(null);
    setForm({ name: '', permissions: [] });
    setModalOpen(true);
  };

  const openEdit = (role) => {
    setEditingRole(role);
    setForm({ name: role.name, permissions: role.permissions ?? [] });
    setModalOpen(true);
  };

  const togglePermission = (perm) => {
    setForm((f) => ({
      ...f,
      permissions: f.permissions.includes(perm)
        ? f.permissions.filter((p) => p !== perm)
        : [...f.permissions, perm],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token || !form.name.trim()) return;
    setSubmitting(true);
    try {
      if (editingRole) {
        await updateRole(token, editingRole.id, { name: form.name.trim(), permissions: form.permissions });
        toast.success('Role updated');
      } else {
        await createRole(token, { name: form.name.trim(), permissions: form.permissions });
        toast.success('Role created');
      }
      setModalOpen(false);
      fetchRoles();
    } catch (err) {
      toast.error(err?.message || 'Failed to save role');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    const role = deleteModal.role;
    if (!token || !role) return;
    try {
      await deleteRole(token, role.id);
      toast.success('Role deleted');
      fetchRoles();
    } catch (err) {
      toast.error(err?.message || 'Failed to delete role');
    } finally {
      setDeleteModal({ open: false, role: null });
    }
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Shield className="h-5 w-5 text-teal-500" /> Roles
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">Create roles and assign permissions</p>
        </div>
        <Button onClick={openCreate} className="bg-teal-600 hover:bg-teal-700">
          <Plus className="h-4 w-4 mr-2" /> Add Role
        </Button>
      </div>
      <Card className="border border-border mt-4">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Role name</TableHead>
                <TableHead>Permissions</TableHead>
                <TableHead className="w-[120px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                    Loading…
                  </TableCell>
                </TableRow>
              ) : (
                roles.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.name}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(r.permissions || []).length === 0 ? (
                          <span className="text-muted-foreground text-sm">—</span>
                        ) : (
                          (r.permissions || []).map((p) => (
                            <Badge key={p} variant="secondary" className="text-xs">
                              {p}
                            </Badge>
                          ))
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(r)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setDeleteModal({ open: true, role: r })} className="text-red-600">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>{editingRole ? 'Edit Role' : 'Add Role'}</DialogTitle>
            <DialogDescription>
              {editingRole ? 'Update role name and permissions.' : 'Create a new role and select permissions.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Role name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Loan Officer"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Permissions</Label>
              <div className="border rounded-xl p-3 max-h-60 overflow-y-auto space-y-4 bg-muted/20">
                {PERMISSION_GROUPS.map((group) => (
                  <div key={group.title} className="space-y-2">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground border-b pb-1">{group.title}</p>
                    <div className="grid grid-cols-2 gap-2">
                      {group.perms.map((perm) => (
                        <label key={perm} className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors">
                          <input
                            type="checkbox"
                            checked={form.permissions.includes(perm)}
                            onChange={() => togglePermission(perm)}
                            className="rounded border-input h-4 w-4"
                          />
                          <span className="text-sm">{perm}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {editingRole ? 'Update Role' : 'Create Role'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <DeleteModal
        open={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, role: null })}
        title="Delete Role"
        description={`Are you sure you want to delete the "${deleteModal.role?.name}" role? This action cannot be reversed and will fail if any employees are currently assigned to this role.`}
        onConfirm={handleDelete}
      />
    </>
  );
};

const DepartmentsSection = () => {
  const { token } = useAuth();
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ open: false, dept: null });

  const fetchDepartments = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await getDepartments(token);
      setDepartments(res?.data?.items ?? []);
    } catch {
      setDepartments([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  const openCreate = () => {
    setEditingDept(null);
    setName('');
    setModalOpen(true);
  };

  const openEdit = (dept) => {
    setEditingDept(dept);
    setName(dept.name);
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token || !name.trim()) return;
    setSubmitting(true);
    try {
      if (editingDept) {
        await updateDepartment(token, editingDept.id, { name: name.trim() });
        toast.success('Department updated');
      } else {
        await createDepartment(token, { name: name.trim() });
        toast.success('Department created');
      }
      setModalOpen(false);
      fetchDepartments();
    } catch (err) {
      toast.error(err?.message || 'Failed to save department');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    const dept = deleteModal.dept;
    if (!token || !dept) return;
    try {
      await deleteDepartment(token, dept.id);
      toast.success('Department deleted');
      fetchDepartments();
    } catch (err) {
      toast.error(err?.message || 'Failed to delete department');
    } finally {
      setDeleteModal({ open: false, dept: null });
    }
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Building2 className="h-5 w-5 text-blue-500" /> Departments
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">Create and manage departments</p>
        </div>
        <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="h-4 w-4 mr-2" /> Add Department
        </Button>
      </div>
      <Card className="border border-border mt-4">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Department name</TableHead>
                <TableHead className="w-[120px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={2} className="text-center text-muted-foreground py-8">
                    Loading…
                  </TableCell>
                </TableRow>
              ) : (
                departments.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell className="font-medium">{d.name}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(d)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setDeleteModal({ open: true, dept: d })} className="text-red-600">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>{editingDept ? 'Edit Department' : 'Add Department'}</DialogTitle>
            <DialogDescription>
              {editingDept ? 'Update department name.' : 'Create a new department.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Department name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Operations"
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {editingDept ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <DeleteModal
        open={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, dept: null })}
        title="Delete Department"
        description={`Are you sure you want to delete the "${deleteModal.dept?.name}" department? This action cannot be reversed and will fail if any employees are currently assigned to this department.`}
        onConfirm={handleDelete}
      />
    </>
  );
};

export default function RoleAndPermission() {
  return (
    <div className="space-y-6" data-testid="role-and-permission-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">
          Role & Permissions
        </h1>
        <p className="text-muted-foreground mt-1">
          Manage employee roles, permissions, and departments
        </p>
      </div>
      <Tabs defaultValue="roles" className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="roles">Roles</TabsTrigger>
          <TabsTrigger value="departments">Departments</TabsTrigger>
        </TabsList>
        <TabsContent value="roles" className="mt-4">
          <RolesSection />
        </TabsContent>
        <TabsContent value="departments" className="mt-4">
          <DepartmentsSection />
        </TabsContent>
      </Tabs>
    </div>
  );
}
